import React, { useEffect, useState } from "react";
import { Grid, LinearProgress, linearProgressClasses, Stack } from "@mui/material";
import styled, { css, keyframes } from "styled-components";
import NoDataPlaceholder from "src/components/no-data-placeholder";
import fetchStatus from "../../utils/api/fetch-status";
import loadingMessages from "../../utils/loading-messages";

const TitleText = styled.p`
    margin: 0;
    font-weight: 600;
    font-size: 14px;
`;

const MainValue = styled.p`
    margin: 0;
    font-size: 69px;
    font-weight: 500;
`;

const HeaderText = styled.p`
    margin: 0 0 12px 0;
`;

const DescriptionText = styled.p`
    margin: 0;
    font-size: 12px;
    font-weight: 200;
`;

const blink = keyframes`
  0%{opacity: 0;}
  50%{opacity: 1;}
  100%{opacity: 0.5;}
`;

const animation = () => css`
    ${blink} 2s infinite ease-out;
`;

const LoadingMessage = styled.div`
    animation: ${animation};
`;

const getRandomMessage = () => {
    return loadingMessages[Math.floor(Math.random() * loadingMessages.length)];
};

const BorderLinearProgress = styled(LinearProgress)(({ theme, value }) => ({
    height: 10,
    borderRadius: 5,
    [`&.${linearProgressClasses.colorPrimary}`]: {
        backgroundColor: theme.palette.grey[theme.palette.mode === "light" ? 200 : 800],
    },
    [`& .${linearProgressClasses.bar}`]: {
        borderRadius: 5,
        backgroundColor: value > 20 ? "#59C36A" : "#ff9800",
    },
}));

const CaseAnalysisMainSection = ({ data, status }) => {
    const [loadingMessage, setLoadingMessage] = useState(getRandomMessage());
    const [intervalId, setIntervalId] = useState(undefined);

    useEffect(() => {
        if (status === fetchStatus.STREAMING && !intervalId) {
            const interval = setInterval(() => {
                setLoadingMessage(getRandomMessage());
            }, 4000);
            setIntervalId(interval);
        }
        if (status !== fetchStatus.STREAMING && intervalId) {
            clearInterval(intervalId);
            setIntervalId(undefined);
            setLoadingMessage(getRandomMessage());
        }
        return () => clearInterval(intervalId);
    }, [status]);

    if (status === fetchStatus.STREAMING) {
        return (
            <Grid item md={12} mt={4}>
                <Stack direction="column" alignItems="center" spacing={2}>
                    <LoadingMessage>{loadingMessage}</LoadingMessage>
                    <LinearProgress sx={{ width: 1 }} />
                </Stack>
            </Grid>
        );
    }
    if (!data || data.length === 0) return null;
    if (data && data.error !== null)
        return (
            <Grid item md={12}>
                <NoDataPlaceholder variant="correlation" errorMessage={data.error} />
            </Grid>
        );

    const overallError = data && data.overall && data.overall.error;
    const smallerFrameError = data && data.smaller_frame && data.smaller_frame.error;

    return (
        <Grid item md={12} mt={4}>
            <Stack direction="row" justifyContent="space-around" spacing={6}>
                {!overallError ? (
                    <Stack direction="column">
                        <TitleText>Overall</TitleText>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Stack direction="row" alignItems="flex-end">
                                <MainValue>{(data.overall.value * 10).toFixed(1)}</MainValue>
                                <HeaderText style={{ marginBottom: "24px" }}>/10</HeaderText>
                            </Stack>
                            <Stack direction="column">
                                <HeaderText
                                    style={{
                                        padding: "6px",
                                        borderRadius: "8px",
                                        border: "1px solid white",
                                        textAlign: "center",
                                    }}
                                >
                                    {data.overall.significance.toUpperCase()}
                                </HeaderText>
                                <BorderLinearProgress
                                    variant="determinate"
                                    value={Math.abs(Math.round(data.overall.value * 100))}
                                    style={{ width: "100px" }}
                                />
                            </Stack>
                        </Stack>
                        <HeaderText>{data.overall.explanation}</HeaderText>
                        <DescriptionText style={{ whiteSpace: "pre-line" }}>
                            {data.overall.description}
                        </DescriptionText>
                    </Stack>
                ) : (
                    <NoDataPlaceholder variant="correlation" errorMessage={overallError} />
                )}
                {!smallerFrameError ? (
                    <Stack direction="column">
                        <TitleText>{data.smaller_frame.frame_name}</TitleText>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                            <Stack direction="row" alignItems="flex-end">
                                <MainValue>{(data.smaller_frame.value * 10).toFixed(1)}</MainValue>
                                <HeaderText style={{ marginBottom: "24px" }}>/10</HeaderText>
                            </Stack>
                            <Stack direction="column">
                                <HeaderText
                                    style={{
                                        padding: "6px",
                                        borderRadius: "8px",
                                        border: "1px solid white",
                                        textAlign: "center",
                                    }}
                                >
                                    {data.smaller_frame.significance.toUpperCase()}
                                </HeaderText>
                                <BorderLinearProgress
                                    variant="determinate"
                                    value={Math.abs(Math.round(data.smaller_frame.value * 100))}
                                    style={{ width: "100px" }}
                                />
                            </Stack>
                        </Stack>

                        <HeaderText>{data.smaller_frame.explanation}</HeaderText>
                        <DescriptionText style={{ whiteSpace: "pre-line" }}>
                            {data.smaller_frame.description}
                        </DescriptionText>
                    </Stack>
                ) : (
                    <NoDataPlaceholder variant="correlation" errorMessage={smallerFrameError} />
                )}
            </Stack>
        </Grid>
    );
};

export default CaseAnalysisMainSection;
